import { Button, buttonRecipe } from "../../basic/Button";
import { Label } from "../../../custom/programs/fields";
import { useFetch } from "../../../../hooks/api";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row, Form } from "react-bootstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckboxField, SelectField } from "../../../../modules/dsp/forms/fields";
import useAdPlanState from "./useAdPlanState";
import { Accordion } from "../../basic/Accordion";
import { useAppSelector } from "../../../../redux/hooks";
import useMerchandisePreferences from "../../../../hooks/useMerchandisePreferences";
import HarvestingConfiguration from "../../../../modules/advertising/components/HarvestingConfiguration";
import api from "../../../../utils/api";
import axios from "axios";
import { AD_TYPES, GOALS } from "./constants";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import ConfirmationModal from "../ConfirmationModal";
import { FaTriangleExclamation } from "react-icons/fa6";
import { successToast, errorToast } from "../../../../utils/toasts";
import { useSimpleCan } from "../../../../config/casl/casl";
import { useGetV4AdvReportByAdInfiniteQueryOptions } from "../../../../modules/perf_reports/api/ads/v4AdvReportByAd";
import { useAdPlanDetails } from "../../../../modules/perf_reports/api/ads/useAdPlanDetails";
import { useDebounce } from "@reactuses/core";
import { Combobox, ComboboxLoadMoreItem } from "../../../fields/base/Combobox";

const formatMatchTypes = (formState) => {
  const matchTypeArray = [];

  if (formState.harvestBroadMatchTypes) {
    matchTypeArray.push("broad");
  }
  if (formState.harvestPhraseMatchTypes) {
    matchTypeArray.push("phrase");
  }
  if (formState.harvestExactMatchTypes) {
    matchTypeArray.push("exact");
  }

  return matchTypeArray;
};

const formatNegativeMatchTypes = (formState) => {
  const negativeMatchTypeArray = [];

  if (formState.negatePhraseMatchTypes) {
    negativeMatchTypeArray.push("phrase");
  }
  if (formState.negateExactMatchTypes) {
    negativeMatchTypeArray.push("exact");
  }

  return negativeMatchTypeArray;
};

const SourceAdCombobox = ({
  label,
  value,
  onChange,
  errorMessage,
  currentAdId,
  selectedLabel,
  isSelectedFetching,
  onOptionSelect,
  isDisabled = false,
}) => {
  const { t } = useTranslation(["advertising", "common"]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [adSearch, setAdSearch] = useState("");
  const debouncedAdSearch = useDebounce(adSearch, 500);
  const hasSearch = Boolean(adSearch.trim());
  const searchTerm = hasSearch ? debouncedAdSearch.trim() : "";
  const isOpen = isMenuOpen || hasSearch;

  const advByAdQuery = useInfiniteQuery(
    useGetV4AdvReportByAdInfiniteQueryOptions({
      pagination: { pageIndex: 0, pageSize: 50 },
      sorting: [],
      filters: [],
      search: searchTerm,
    }),
  );
  const allAdvertisements = useMemo(() => {
    return advByAdQuery.data?.pages.flatMap((page) => page?.data ?? []) ?? [];
  }, [advByAdQuery.data?.pages]);
  const advertisementOptions = useMemo(() => {
    const options =
      allAdvertisements
        ?.filter((ad) => {
          return (
            !ad.is_composite &&
            ad.ad_id !== currentAdId &&
            ![AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(ad.ad_type)
          );
        })
        ?.map((ad) => ({
          label: `${ad.ad_name} (${ad.ad_id})`,
          value: ad.ad_id,
        })) ?? [];
    return options;
  }, [allAdvertisements, currentAdId]);
  const inputValue = hasSearch ? adSearch : (selectedLabel ?? "");
  const placeholder =
    isSelectedFetching && !selectedLabel ? t(($) => $.common["Loading..."]) : t(($) => $.advertising["Search ads..."]);

  const listBoxFooter =
    advByAdQuery.hasNextPage ?
      <ComboboxLoadMoreItem
        isLoading={advByAdQuery.isFetchingNextPage}
        onLoadMore={() => {
          if (advByAdQuery.hasNextPage && !advByAdQuery.isFetchingNextPage) {
            void advByAdQuery.fetchNextPage();
          }
        }}
      />
    : null;

  return (
    <Form.Group>
      <Combobox
        label={label}
        isDisabled={isDisabled}
        items={advertisementOptions}
        value={hasSearch ? null : (value ?? null)}
        inputValue={inputValue}
        defaultFilter={() => true}
        isOpen={isOpen}
        errorMessage={errorMessage}
        placeholder={placeholder}
        listBoxFooter={listBoxFooter}
        onOpenChange={(nextOpen) => {
          if (hasSearch && !nextOpen) {
            return;
          }
          setIsMenuOpen(nextOpen);
        }}
        onChange={(key) => {
          if (!key || key === "__loading__" || key === "__empty__") {
            return;
          }
          const parsed = Number(key);
          const nextValue = Number.isNaN(parsed) ? key : parsed;
          const selectedOption = advertisementOptions.find((option) => String(option.value) === String(nextValue));
          if (selectedOption?.label) {
            onOptionSelect?.(nextValue, selectedOption.label);
          }
          onChange(nextValue);
          setAdSearch("");
          setIsMenuOpen(false);
        }}
        onInputChange={(nextValue) => {
          if (!hasSearch && selectedLabel && nextValue === selectedLabel) {
            return;
          }
          if (!nextValue.trim()) {
            setAdSearch("");
            onChange(null);
            return;
          }
          setAdSearch(nextValue);
          setIsMenuOpen(true);
        }}
        isLoading={(hasSearch && advByAdQuery.isFetching) || isSelectedFetching}
      />
    </Form.Group>
  );
};

const formatRequestBody = (formState, adParams) => {
  return {
    state: formState.harvestingEnabled ? "enabled" : "disabled",
    target_dest_ad_group: formState.targetingDestinationAdGroup,
    source_advertisement: formState.sourceAd,
    dest_advertisement: adParams.id,
    keyword_dest_ad_group: formState.keywordDestinationAdGroup,
    source_target_negation: formState.negateTargetsInSource,
    source_keyword_negation: formState.negateKeywordsInSource,
    dest_match_types: formatMatchTypes(formState),
    source_neg_match_types: formatNegativeMatchTypes(formState),
  };
};

const DEFAULT_HARVESTING_VALUES = {
  harvestingEnabled: false,
  sourceAd: null,
  enableTargetHarvesting: false,
  targetingDestinationAdGroup: null,
  negateTargetsInSource: false,
  enableKeywordHarvesting: false,
  keywordDestinationAdGroup: null,
  negateKeywordsInSource: false,
  harvestBroadMatchTypes: false,
  harvestPhraseMatchTypes: false,
  harvestExactMatchTypes: false,
  negatePhraseMatchTypes: false,
  negateExactMatchTypes: false,
};

const formatDefaultValues = (adPlanParams, harvestingConfig) => {
  if (!harvestingConfig) {
    return {
      ...DEFAULT_HARVESTING_VALUES,
      enableTargetHarvesting: adPlanParams.asinHarvestingEnabled,
      enableKeywordHarvesting: adPlanParams.keywordHarvestingEnabled,
    };
  }

  const {
    state,
    target_dest_ad_group,
    keyword_dest_ad_group,
    dest_match_types,
    source_advertisement,
    source_keyword_negation,
    source_target_negation,
    source_neg_match_types,
    dest_ad_keyword_harvesting_enabled,
    dest_ad_asin_harvesting_enabled,
  } = harvestingConfig ?? {};

  return {
    harvestingEnabled: state === "enabled",
    sourceAd: source_advertisement,
    enableTargetHarvesting: dest_ad_asin_harvesting_enabled,
    targetingDestinationAdGroup: target_dest_ad_group,
    negateTargetsInSource: source_target_negation,
    enableKeywordHarvesting: dest_ad_keyword_harvesting_enabled,
    keywordDestinationAdGroup: keyword_dest_ad_group,
    negateKeywordsInSource: source_keyword_negation,
    harvestBroadMatchTypes: dest_match_types.includes("broad"),
    harvestPhraseMatchTypes: dest_match_types.includes("phrase"),
    harvestExactMatchTypes: dest_match_types.includes("exact"),
    negatePhraseMatchTypes: source_neg_match_types.includes("phrase"),
    negateExactMatchTypes: source_neg_match_types.includes("exact"),
  };
};

const AdvertisementHarvestingConfiguration = ({
  advertisement,
  adPlanParams,
  adHarvestingConfigData,
  isAdArchived = false,
}) => {
  const { t } = useTranslation(["advertising", "common"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const hasNTMHarvestingModule = useModuleEnabled("EXTERNAL_HARVESTING");
  const { preferences } = useMerchandisePreferences();
  const queryClient = useQueryClient();
  // Archived ads are read-only: disable every field and the update button.
  const userCanUpdateAds = useSimpleCan("update", "Ad") && !isAdArchived;
  const [selectedSourceMeta, setSelectedSourceMeta] = useState(null);

  const currentAdId = adPlanParams.id;
  const { adPlan: state, onChange } = useAdPlanState(
    {},
    marketPlace,
    advertisement?.category_id,
    adPlanParams.id,
    preferences.acos_or_roas === "roas",
    user.organization_type,
  );

  const defaultValues = useMemo(() => {
    return formatDefaultValues(adPlanParams, adHarvestingConfigData);
  }, [adHarvestingConfigData, adPlanParams]);

  const {
    control,
    formState: { errors },
    setValue,
    setError,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const formState = useWatch({ control });
  const sourceAdDetailsQuery = useQuery(
    useAdPlanDetails(
      {
        adId: formState.sourceAd,
      },
      {
        enabled: Boolean(formState.sourceAd),
      },
    ),
  );
  const sourceAdDetails = sourceAdDetailsQuery.data?.data?.[0] ?? null;

  const isNTM = adPlanParams.goal === GOALS.IMPORTED_ADS.value;

  const isSourceAdManaged = useMemo(() => {
    return sourceAdDetails?.managed === "Yes";
  }, [sourceAdDetails]);

  const isSelectedFetching =
    sourceAdDetailsQuery.isFetching && (!sourceAdDetailsQuery.data || sourceAdDetailsQuery.isPlaceholderData);

  // If an ad not in the base 50 is selected we don't have it's label due to the clearance
  // this is a way to save / display that label during the source ad query fetch
  const selectedSourceLabel = useMemo(() => {
    const selectedValue = formState.sourceAd;
    if (!selectedValue) {
      return "";
    }
    if (selectedSourceMeta && String(selectedSourceMeta.id) === String(selectedValue)) {
      return selectedSourceMeta.label ?? "";
    }
    if (sourceAdDetails?.ad_name) {
      const sourceAdId = sourceAdDetails.ad_id ?? sourceAdDetails.id ?? selectedValue;
      return `${sourceAdDetails.ad_name} (${sourceAdId})`;
    }
    return "";
  }, [formState.sourceAd, selectedSourceMeta, sourceAdDetails]);
  const validateRequiredFields = () => {
    if (isNTM) {
      if (formState.harvestingEnabled && !formState.sourceAd) {
        setError("sourceAd", {
          type: "custom",
          message: t(($) => $.advertising["A source ad is required"]),
        });
        return false;
      }
      if (formState.enableKeywordHarvesting && !formState.keywordDestinationAdGroup) {
        setError("keywordDestinationAdGroup", {
          type: "custom",
          message: t(($) => $.advertising["A destination ad group is required"]),
        });
        return false;
      }

      if (formState.enableTargetHarvesting && !formState.targetingDestinationAdGroup) {
        setError("targetingDestinationAdGroup", {
          type: "custom",
          message: t(($) => $.advertising["A destination ad group is required"]),
        });
        return false;
      }
    }

    return true;
  };

  const updateAdHarvestingSettings = useMutation({
    mutationFn: async () => {
      const adPlanParamsBody = {};
      if (
        adHarvestingConfigData?.dest_ad_asin_harvesting_enabled !== formState.enableTargetHarvesting ||
        (!adHarvestingConfigData && adPlanParams.asinHarvestingEnabled !== formState.enableTargetHarvesting)
      ) {
        adPlanParamsBody["asin_harvesting"] = formState.enableTargetHarvesting;
      }

      if (
        adHarvestingConfigData?.dest_ad_keyword_harvesting_enabled !== formState.enableKeywordHarvesting ||
        (!adHarvestingConfigData && adPlanParams.keywordHarvestingEnabled !== formState.enableKeywordHarvesting)
      ) {
        adPlanParamsBody["keyword_harvesting"] = formState.enableKeywordHarvesting;
      }

      if (
        (adHarvestingConfigData?.state === "enabled" && !formState.harvestingEnabled) ||
        (adHarvestingConfigData?.state === "disabled" && formState.harvestingEnabled) ||
        !adHarvestingConfigData
      ) {
        adPlanParamsBody["harvesting_external"] = formState.harvestingEnabled;
      }

      if (Object.keys(adPlanParamsBody).length) {
        await updateAdPlanParams.mutateAsync({ harvestingFields: adPlanParamsBody, showNotification: false });
      }

      const body = formatRequestBody(formState, advertisement);

      if (adHarvestingConfigData?.id) {
        return await axios.put(`/harvesting_relationships/${adHarvestingConfigData.id}/`, body);
      }

      return await axios.post("/harvesting_relationships/", body);
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.advertising["Harvesting settings successfully updated"]),
      });
      queryClient.invalidateQueries({
        queryKey: ["adHarvestingConfig", advertisement.id],
      });
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to update harvesting settings"]), error });
    },
  });

  const updateAdPlanParams = useMutation({
    mutationFn: async ({ harvestingFields }) => {
      const body = {
        id: advertisement.id,
        custom_api_request: "update_advertisement",
        state: advertisement.state,
        ad_control_fields: harvestingFields,
      };
      return await api.put("/api/gvads/advproduct/", body);
    },

    onSuccess: (_, variables) => {
      if (variables.showNotification) {
        successToast({
          message: t(($) => $.advertising["Updating Media Plan"]),
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["media-plan", advertisement.id],
      });
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Update Media Plan"]), error });
    },
  });

  const { data: adGroupData } = useFetch(
    ["adGroups", formState.sourceAd],
    "/api/ad_groups",
    {
      advertisement: advertisement.id,
    },
    {
      enabled: !!advertisement.id,
      select: (res) => res.data ?? [],
    },
  );

  const adGroupOptions = useMemo(() => {
    return (
      adGroupData?.map((adGroup) => {
        return { value: adGroup.ad_group_id, label: adGroup.ad_group_name };
      }) ?? []
    );
  }, [adGroupData]);

  if (adPlanParams.goal !== GOALS.IMPORTED_ADS.value || !hasNTMHarvestingModule) {
    return null;
  }

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Row>
          <Col xs={12} className="p-4 text-center">
            <h3 className="fs-1 fw-bold">{t(($) => $.advertising["Harvesting Configuration"])}</h3>
            <p className="fs-standard">
              {t(
                ($) =>
                  $.advertising[
                    "Enable harvesting from an alternate media plan to customize the sources for gathering your desired targets and keywords."
                  ],
              )}
            </p>
          </Col>

          <Col xs={12} lg={{ span: 10, offset: 1 }}>
            <Row>
              <Form.Label className="fw-bold fs-2 px-2 pt-3">{t(($) => $.advertising["Overview"])}</Form.Label>
              <Col xs={12} className="py-3">
                <CheckboxField
                  label={t(($) => $.advertising["Enable Harvesting from Another Media Plan"])}
                  fieldName={"harvestingEnabled"}
                  control={control}
                  disabled={!userCanUpdateAds}
                />
              </Col>
              {formState.harvestingEnabled && (
                <Col xs={12} lg={4} className="py-3">
                  <Controller
                    name={"sourceAd"}
                    control={control}
                    render={({ field }) => (
                      <SourceAdCombobox
                        label={t(($) => $.advertising["Source Media Plan"])}
                        value={field.value}
                        onChange={(nextValue) => {
                          field.onChange(nextValue);
                          if (nextValue == null) {
                            setSelectedSourceMeta(null);
                          }
                        }}
                        currentAdId={currentAdId}
                        selectedLabel={selectedSourceLabel}
                        isSelectedFetching={isSelectedFetching}
                        onOptionSelect={(nextValue, nextLabel) => {
                          setSelectedSourceMeta({ id: nextValue, label: nextLabel });
                        }}
                        errorMessage={errors.sourceAd?.message}
                        isDisabled={!userCanUpdateAds}
                      />
                    )}
                  />
                </Col>
              )}
              {/* Keyword Negation Settings */}
              <hr className="mt-4" />
              <Form.Label className="fw-bold fs-2 px-2 pt-3">{t(($) => $.advertising["Keywords"])}</Form.Label>
              {formState.harvestingEnabled && (
                <Col xs={12} className="py-3">
                  <CheckboxField
                    label={t(($) => $.advertising["Enable Keyword Harvesting"])}
                    fieldName={"enableKeywordHarvesting"}
                    control={control}
                    disabled={!userCanUpdateAds}
                  />
                </Col>
              )}
              {formState.harvestingEnabled && formState.enableKeywordHarvesting && (
                <>
                  <Col xs={12} lg={4} className="py-3">
                    <SelectField
                      label={t(($) => $.advertising["Destination Ad Group"])}
                      options={
                        (
                          formState?.enableTargetHarvesting &&
                          formState?.targetingDestinationAdGroup &&
                          !adHarvestingConfigData?.id
                        ) ?
                          adGroupOptions?.filter((adGroup) => adGroup.value !== formState?.targetingDestinationAdGroup)
                        : adGroupOptions
                      }
                      fieldName={"keywordDestinationAdGroup"}
                      control={control}
                      error={errors.keywordDestinationAdGroup}
                      errorClassNames={"d-block fs-5 pt-3"}
                      isClearable={true}
                      disabled={!userCanUpdateAds}
                    />
                  </Col>
                  <Row className="px-0 py-4">
                    <Col xs={12} lg={4} lassName="pb-3">
                      <Label>{t(($) => $.advertising["Match Types"])}</Label>
                      <div className="d-flex gap-5 pt-2">
                        <CheckboxField
                          label={t(($) => $.advertising["Broad"])}
                          fieldName={"harvestBroadMatchTypes"}
                          control={control}
                          disabled={!userCanUpdateAds}
                        />
                        <CheckboxField
                          label={t(($) => $.advertising["Phrase"])}
                          fieldName={"harvestPhraseMatchTypes"}
                          control={control}
                          disabled={!userCanUpdateAds}
                        />
                        <CheckboxField
                          label={t(($) => $.advertising["Exact"])}
                          fieldName={"harvestExactMatchTypes"}
                          control={control}
                          disabled={!userCanUpdateAds}
                        />
                      </div>
                    </Col>

                    <Col xs={12} lg={3} className="pb-3 pt-2">
                      <Label>{t(($) => $.advertising["Negate in Source"])}</Label>
                      <CheckboxField
                        label={t(($) => $.advertising["Negate in Source"])}
                        fieldName={"negateKeywordsInSource"}
                        control={control}
                        disabled={!userCanUpdateAds}
                        onChange={(checked) => {
                          if (!checked) {
                            setValue("negatePhraseMatchTypes", false);
                            setValue("negateExactMatchTypes", false);
                          }
                        }}
                      />
                    </Col>
                    {formState.negateKeywordsInSource && (
                      <>
                        {isSourceAdManaged ?
                          <Col xs={12} lg={4} className="pb-3 pt-2">
                            <Label>{t(($) => $.advertising["Negative Match Types"])}</Label>
                            <div className="d-flex gap-3">
                              <CheckboxField
                                label={t(($) => $.advertising["Negative Phrase"])}
                                fieldName={"negatePhraseMatchTypes"}
                                control={control}
                                disabled={!userCanUpdateAds}
                              />
                              <CheckboxField
                                label={t(($) => $.advertising["Negative Exact"])}
                                fieldName={"negateExactMatchTypes"}
                                control={control}
                                disabled={!userCanUpdateAds}
                              />
                            </div>
                          </Col>
                        : <Col xs={12} lg={{ span: 8, offset: 4 }} className="px-4">
                            {!isSourceAdManaged ?
                              <small className="px-3 text-muted">
                                <FaTriangleExclamation />{" "}
                                {t(
                                  ($) =>
                                    $.advertising[
                                      "Keyword negation will be skipped in the source media plan as it is not managed by Trellis"
                                    ],
                                )}
                              </small>
                            : null}
                          </Col>
                        }
                      </>
                    )}
                  </Row>
                </>
              )}
              {/* Target Negation Settings */}
              <hr className="mt-4" />
              <Form.Label className="fw-bold fs-2 px-2 pt-3">{t(($) => $.advertising["Targets"])}</Form.Label>
              {formState.harvestingEnabled && (
                <>
                  <Col xs={12} className="py-3">
                    <CheckboxField
                      label={t(($) => $.advertising["Enable Target Harvesting"])}
                      fieldName={"enableTargetHarvesting"}
                      control={control}
                      disabled={!userCanUpdateAds}
                    />
                  </Col>
                  {formState.enableTargetHarvesting && (
                    <>
                      <Col xs={12} lg={4} className="py-3">
                        <SelectField
                          label={t(($) => $.advertising["Destination Ad Group"])}
                          options={
                            (
                              formState.enableKeywordHarvesting &&
                              formState?.keywordDestinationAdGroup &&
                              !adHarvestingConfigData?.id
                            ) ?
                              adGroupOptions?.filter(
                                (adGroup) => adGroup.value !== formState?.keywordDestinationAdGroup,
                              )
                            : adGroupOptions
                          }
                          fieldName={"targetingDestinationAdGroup"}
                          control={control}
                          error={errors.targetingDestinationAdGroup}
                          errorClassNames={"d-block fs-5 pt-3"}
                          isClearable={true}
                          disabled={!userCanUpdateAds}
                        />
                      </Col>
                      <Col xs={12} className="py-3">
                        <Label>{t(($) => $.advertising["Negate in Source"])}</Label>
                        <CheckboxField
                          label={t(($) => $.advertising["Negate in Source"])}
                          fieldName={"negateTargetsInSource"}
                          control={control}
                          disabled={!userCanUpdateAds}
                        />
                      </Col>
                      <Col xs={12} className="px-4">
                        {!isSourceAdManaged && formState.negateTargetsInSource ?
                          <small className="px-2 text-muted">
                            <FaTriangleExclamation />{" "}
                            {t(
                              ($) =>
                                $.advertising[
                                  "Target negation will be skipped in the source media plan as it is not managed by Trellis"
                                ],
                            )}
                          </small>
                        : null}
                      </Col>
                    </>
                  )}
                </>
              )}
            </Row>
          </Col>
          <Col xs={12} className="text-center py-5 d-flex justify-content-center">
            <ConfirmationModal
              title={t(($) => $.advertising["Revert Harvesting Configuration Changes"])}
              confirmButtonText={t(($) => $.advertising["Confirm"])}
              confirmationMessage={
                <p className="fs-4 w-75 mx-auto">
                  {t(
                    ($) =>
                      $.advertising[
                        "Are you sure you want to revert all changes made to this media plan's harvesting configuration?"
                      ],
                  )}
                </p>
              }
              onConfirm={() => {
                reset(defaultValues);
              }}
              inlineModal={true}
              modalButtonText={
                <button className={buttonRecipe({ variant: "secondary" })} style={{ marginRight: "5rem" }}>
                  {t(($) => $.common.actions.Cancel)}
                </button>
              }
              inlineButtonContainerStyles={{}}
            />

            <Button
              onPress={() => {
                const isValid = validateRequiredFields();
                if (isValid) {
                  updateAdHarvestingSettings.mutate();
                }
              }}
              isPending={updateAdHarvestingSettings.isPending}
              isDisabled={Object.keys(errors).length || !userCanUpdateAds}
            >
              {t(($) => $.advertising["Update"])}
            </Button>
          </Col>
        </Row>
      </form>
      <Row>
        <Col xs={12} lg={{ span: 10, offset: 1 }}>
          <Accordion title={t(($) => $.advertising["Harvesting Rules"])}>
            <HarvestingConfiguration
              onChange={onChange}
              state={state}
              disableSubmitButton={updateAdHarvestingSettings.isPending || updateAdPlanParams.isPending}
              textColor={"#152748"}
              onSubmit={() => {
                const harvestingAdControlFields = {
                  harvesting_setting_override: state.manualHarvestingEnabled,
                  harvesting_clicks:
                    isNaN(Number.parseFloat(state.harvestingClicks)) ? null : Number.parseFloat(state.harvestingClicks),
                  harvesting_conversions:
                    isNaN(Number.parseFloat(state.harvestingConversions)) ? null : (
                      Number.parseFloat(state.harvestingConversions)
                    ),
                  harvesting_days:
                    isNaN(Number.parseFloat(state.harvestingDays)) ? null : Number.parseFloat(state.harvestingDays),
                  harvesting_view_impressions:
                    isNaN(Number.parseFloat(state.harvestingViewImpressions)) ? null : (
                      Number.parseFloat(state.harvestingViewImpressions)
                    ),
                };
                updateAdPlanParams.mutate({ harvestingFields: harvestingAdControlFields, showNotification: true });
              }}
              readOnly={!userCanUpdateAds}
            />
          </Accordion>
        </Col>
      </Row>
    </>
  );
};

export default AdvertisementHarvestingConfiguration;
